import { useState } from 'react';

import clsx from 'clsx';

import Accordion from 'components/Accordion';
import CircularProgress from 'components/CircularProgress';
import GoodRecommendations from 'components/GoodRecommendations';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import Listing from 'components/Listing';
import Skeleton from 'components/Skeleton';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';

import classes from './MoreLikeThisSection.module.scss';

const STATUS = {
  initial: 0,
  loading: 1,
  success: 2,
  error: 3,
};

const MoreLikeThisSection = ({ listing, fallbackAlternatives = [] }) => {
  const [status, setStatus] = useState(STATUS.initial);
  const [alternatives, setAlternatives] = useState([]);
  const { viewport } = useAppContext();
  const { sendEvent } = useEventContext({
    engagementComponent: 'product_card-alternative',
  });

  const highlyViewedEventString = listing.most_visited
    ? ' | highly-viewed'
    : '';

  const listingSize = ['xs', 'sm', 'md'].includes(viewport) ? 2 : 3;

  const handleExpandClick = (event, { isExpanded }) => {
    sendEvent({
      engagementDetails: 'expansion-panel' + highlyViewedEventString,
      engagementDestination: isExpanded ? 'expand-card' : 'collapse-card',
      name: 'engagement_product_card',
    });
    if (status === STATUS.initial) {
      setStatus(STATUS.loading);
      fetch(`/apiui/similarAlternatives?slug=${listing.slug}`)
        .then((result) => result.json())
        .then((result) => {
          if (result.length > 0) {
            setStatus(STATUS.success);
            setAlternatives(result);
          } else {
            setStatus(STATUS.error);
          }
        })
        .catch(() => {
          setStatus(STATUS.error);
        });
    }
  };

  return (
    <>
      <Accordion
        className={clsx('MoreLikeThisSection', classes.root)}
        summary={
          <span className={classes.summaryHeadline}>
            {status === STATUS.loading ? (
              <>
                <CircularProgress
                  color="inherit"
                  size={24}
                  className={classes.circularProgress}
                />
                Loading
              </>
            ) : (
              <>
                <Icon
                  className={classes.summaryIcon}
                  icon="user-group-solid"
                  prefix="fa"
                  size={20}
                />
                Users also considered
              </>
            )}
          </span>
        }
        summaryClassname={classes.summaryWrapper}
        details={
          alternatives.length > 0 ? (
            <Grid container={true} spacing={2}>
              {alternatives.slice(0, listingSize).map((similarAlternative) => (
                <Grid
                  xs={12}
                  sm={6}
                  lg={4}
                  item={true}
                  key={`${listing.id}-${similarAlternative.id}`}
                >
                  <Listing.Card className={classes.listingCard}>
                    <Listing.Alternative listing={similarAlternative} />
                  </Listing.Card>
                </Grid>
              ))}
              <Grid xs={12} item={true} className={classes.goodRecommendation}>
                <GoodRecommendations />
              </Grid>
            </Grid>
          ) : status === STATUS.error ? (
            <Grid container={true} spacing={2}>
              <Grid xs={12}>No similar alternatives found</Grid>
            </Grid>
          ) : (
            <Grid container={true} spacing={2}>
              {[...new Array(listingSize)].map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid
                  xs={12}
                  sm={6}
                  lg={4}
                  item={true}
                  key={`${listing.id}-skeleton-${index}`}
                >
                  <Skeleton variant="rect" height={206} />
                </Grid>
              ))}
              <Grid xs={12} item={true} className={classes.goodRecommendation}>
                <Skeleton variant="rect" width={242} height={36} />
              </Grid>
            </Grid>
          )
        }
        onExpandClick={handleExpandClick}
      />
      {fallbackAlternatives.length > 0 && (
        <noscript>
          <section aria-label="Users also considered">
            <Grid container={true} spacing={2}>
              {fallbackAlternatives.map((similarAlternative) => (
                <Grid
                  xs={12}
                  sm={6}
                  lg={4}
                  item={true}
                  key={`${listing.id}-fallback-${similarAlternative.name}`}
                >
                  <Listing.Card className={classes.listingCard}>
                    <Listing.Alternative listing={similarAlternative} />
                  </Listing.Card>
                </Grid>
              ))}
            </Grid>
          </section>
        </noscript>
      )}
    </>
  );
};

export default MoreLikeThisSection;
